import React, { useEffect, useState } from "react";
import { useAuth } from "../../useAuth";
import { canViewActions, canViewControls, canViewRisks } from "../../accessControl";
import { getRiskHeatmap } from "../../riskService";
import { getActionsHealthSnapshot } from "../../api/actions.api";
import { getControlsHealth } from "../../api/controls.api";
import Button from "../../ui/Button";
import { BarChart, DonutChart } from "./SummaryCharts";
import { actionStatusSeries, controlAttentionSeries, riskBandSeries, validateHeatmap, chartScopeKey } from "./chartData";

const payload = (response) => response?.data ?? response;
const loadRisks = async () => {
  const data = payload(await getRiskHeatmap("residual"));
  validateHeatmap(data);
  return data;
};
const loadControls = async () => {
  const data = payload(await getControlsHealth());
  for (const key of ["total_controls", "attention_controls", "ineffective_controls", "overdue_reviews", "overdue_tests", "unrated_controls", "missing_evidence"]) {
    if (!Number.isInteger(data?.[key]) || data[key] < 0) throw new Error("Missing control counts");
    if (key !== "total_controls" && data[key] > data.total_controls) throw new Error("Inconsistent control counts");
  }
  return data;
};
const loadActions = async () => {
  const today = new Date();
  const asOf = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const data = payload(await getActionsHealthSnapshot({ as_of: asOf }));
  if (!Array.isArray(data?.status_breakdown) || !Number.isInteger(data.total_actions) || data.total_actions < 0
    || ["overdue_actions", "due_soon_actions"].some((key) => !Number.isInteger(data[key]) || data[key] < 0 || data[key] > data.total_actions)
    || data.status_breakdown.some((item) => !Number.isInteger(item.count) || item.count < 0 || !(item.label || item.value))
    || data.status_breakdown.reduce((sum, item) => sum + item.count, 0) !== data.total_actions) {
    throw new Error("Incomplete action breakdown");
  }
  return data;
};

// Cards load independently. A failed/forbidden request never appears as zero.
function DataCard({ title, load, refresh, onOpen, children }) {
  const [state, setState] = useState({ loading: true });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ loading: true });
    Promise.resolve().then(() => active ? load() : null).then((response) => {
      const data = response?.data ?? response;
      if (!data || typeof data !== "object") throw new Error("Missing chart data");
      if (active) setState({ data, loadedAt: new Date() });
    }).catch(() => { if (active) setState({ error: true }); });
    return () => { active = false; };
  }, [load, refresh, retry]);
  return <section className="glyder-chart-card" aria-label={title}>
    <h3>{title}</h3>
    {state.loading && <p role="status" className="glyder-chart-note">Loading chart…</p>}
    {state.error && <div><p role="alert" className="glyder-chart-error">Unable to load this chart.</p><Button variant="secondary" onClick={() => setRetry((value) => value + 1)}>Retry</Button></div>}
    {state.data && <>
      {children(state.data)}
      {[state.data.scope_note, state.data.completeness_warning, state.data.limitation_note].filter(Boolean).map((note, index) => <p className="glyder-chart-note" key={index}>{note}</p>)}
      <p className="glyder-chart-note">{state.data.as_of ? `As at ${state.data.as_of}. ` : ""}{state.data.timezone ? `Timezone: ${state.data.timezone}. ` : ""}{state.data.generated_at ? `Snapshot generated ${state.data.generated_at}. ` : ""}Updated {state.loadedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.</p>
    </>}
    <div className="glyder-chart-footer"><Button variant="secondary" onClick={onOpen}>Open {title.startsWith("Residual") ? "Risks" : title.startsWith("Action") ? "Actions" : "Controls"}</Button></div>
  </section>;
}

export default function CompanyOverview({ onNavigate }) {
  const { user, token, accessReady = false } = useAuth();
  const [refresh, setRefresh] = useState(0);
  const riskAccess = accessReady && !!token && canViewRisks(user);
  const actionAccess = accessReady && !!token && canViewActions(user);
  const controlAccess = accessReady && !!token && canViewControls(user);
  // Remount and clear all fetched values when the authenticated scope changes.
  const scopeKey = chartScopeKey(user, token);
  return <div>
    <div className="glyder-chart-heading">
      <div><h2>Company Overview</h2><p>A bird’s-eye view of risk, actions and control attention.</p><p className="glyder-chart-note">Charts include the records available to your account.</p></div>
      <Button variant="secondary" onClick={() => setRefresh((value) => value + 1)}>Refresh charts</Button>
    </div>
    <div className="glyder-chart-grid" key={scopeKey}>
      {riskAccess && <DataCard title="Residual risk distribution" load={loadRisks} refresh={refresh} onOpen={() => onNavigate("risks")}>
        {(data) => <><p className="glyder-chart-note">Assessed risks in the heatmap, using your configured risk bands. Unscored risks are excluded. The heatmap source considers up to 10,000 risks.</p><DonutChart label="Residual risk distribution" centreLabel="assessed risks" series={riskBandSeries(data.grid || data, data.cells, data.matrix_config)} emptyMessage="No assessed risks to display." /></>}
      </DataCard>}
      {actionAccess && <DataCard title="Action status" load={loadActions} refresh={refresh} onOpen={() => onNavigate("actions")}>
        {(data) => <><p className="glyder-chart-note">Action status across the current health snapshot.</p><DonutChart label="Action status" centreLabel="actions" series={actionStatusSeries(data)} emptyMessage="No action status data to display." /><p className="glyder-chart-note"><strong>{data.overdue_actions ?? "—"}</strong> overdue · <strong>{data.due_soon_actions ?? "—"}</strong> due soon. These figures overlap with statuses.</p></>}
      </DataCard>}
      {controlAccess && <DataCard title="Control attention" load={loadControls} refresh={refresh} onOpen={() => onNavigate("controls")}>
        {(data) => <><p className="glyder-chart-note"><strong>{data.attention_controls ?? "—"}</strong> of <strong>{data.total_controls ?? "—"}</strong> controls need attention.</p><BarChart label="Control attention counts" series={controlAttentionSeries(data)} /><p className="glyder-chart-note">A control can appear in several bars. Counts are not added together.</p></>}
      </DataCard>}
    </div>
    {!accessReady && <p role="status">Loading your access…</p>}
    {accessReady && !riskAccess && !actionAccess && !controlAccess && <p className="glyder-chart-note">No chart data is available with your current module access.</p>}
  </div>;
}
